/**
 * Step 2: what the legacy ticket is, and how it goes into the ITSM.
 *
 * Shows the legacy facts and posts, stops hard when the ticket was already
 * migrated, and lets the reviewer settle two things before OK: the
 * classification (IN Service/Category from the Ivanti taxonomy, or an RO
 * offering — the model's suggestion preselected where it is on the lists) and
 * the customer (the raised-by person when Ivanti knows them).
 */

import { ExternalLink, Icon, TriangleAlert } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  DescriptionList,
  Field,
  Radio,
  RadioGroup,
  Typography,
} from "@norican/react";
import { useState } from "react";
import { Link } from "react-router";

import { SectionCard, useDateFormatter } from "@/components/common";
import { EmployeePicker } from "@/features/tickets/components/employee-picker";
import { useT } from "@/i18n";
import type { LegacySummary } from "@/lib/api";

import {
  attachmentsOverview,
  categoriesFor,
  type Classification,
  type Customer,
  defaultCustomer,
  initialClassification,
  isClassificationComplete,
  type MigrationKindCode,
  offeringHint,
} from "../lib/migrate-helpers";
import { OptionSelect } from "./option-select";

export interface SummaryStepProps {
  summary: LegacySummary;
  onBack: () => void;
  onOk: (classification: Classification, customer: Customer) => void;
}

export function SummaryStep({ summary, onBack, onOk }: SummaryStepProps) {
  const { t } = useT("tickets");
  const { formatDateTime } = useDateFormatter();
  const [classification, setClassification] = useState<Classification>(() =>
    initialClassification(summary),
  );
  const [customer, setCustomer] = useState<Customer | null>(() => defaultCustomer(summary));
  const [noEmail, setNoEmail] = useState(false);

  const already = summary.already_migrated ?? null;
  const suggestion = summary.suggestion;
  const categories = categoriesFor(summary.taxonomy, classification.service);
  const ready = already === null && customer !== null && isClassificationComplete(classification);
  const offerings = summary.offerings ?? [];
  const hint = offeringHint(offerings.length, suggestion);
  const overview = attachmentsOverview(summary);
  const attachmentParts = [
    overview.images > 0 ? t("migrate.summary.attachmentsImages", { n: overview.images }) : "",
    overview.files > 0 ? t("migrate.summary.attachmentsFiles", { n: overview.files }) : "",
    overview.documents > 0
      ? t("migrate.summary.attachmentsDocuments", { n: overview.documents })
      : "",
    overview.documentsManual ? t("migrate.summary.attachmentsDocumentsManual") : "",
  ].filter(Boolean);

  const facts = [
    {
      label: t("migrate.summary.status"),
      value: [summary.status, summary.stage].filter(Boolean).join(" · "),
    },
    { label: t("migrate.summary.legacyCategory"), value: summary.legacy_category },
    { label: t("migrate.summary.system"), value: summary.system },
    { label: t("migrate.summary.company"), value: summary.company },
    { label: t("migrate.summary.priority"), value: summary.priority },
    {
      label: t("migrate.summary.raisedBy"),
      value: summary.raised_by.email
        ? `${summary.raised_by.name} (${summary.raised_by.email})`
        : summary.raised_by.name,
    },
    { label: t("migrate.summary.responsible"), value: summary.responsible },
    { label: t("migrate.summary.created"), value: formatDateTime(summary.created_at ?? null) },
    // The full picture before OK: what comes across, and what has to be added
    // by hand on the next screen.
    {
      label: t("migrate.summary.attachments"),
      value:
        attachmentParts.length > 0
          ? attachmentParts.join(" · ")
          : t("migrate.summary.attachmentsNone"),
    },
  ].filter((item) => (item.value ?? "") !== "");

  return (
    <div className="flex flex-col gap-6">
      {already === null ? null : (
        <Alert
          tone="warning"
          title={t("migrate.summary.alreadyTitle")}
          icon={<Icon icon={TriangleAlert} size="sm" tone="warning" />}
        >
          <div className="flex flex-col gap-2">
            <span>
              {t("migrate.summary.alreadyBody", {
                number: already.ticket_number,
                date: formatDateTime(already.migrated_at),
                by: already.migrated_by,
              })}
            </span>
            <Link to={already.url_path} className="text-accent-fg-subtle hover:underline">
              {t("migrate.summary.openTicket", { number: already.ticket_number })}
            </Link>
          </div>
        </Alert>
      )}

      <SectionCard
        title={t("migrate.summary.title", { number: summary.legacy_number })}
        description={summary.subject}
        actions={
          <a
            href={summary.legacy_url}
            target="_blank"
            rel="noreferrer"
            className="text-accent-fg-subtle inline-flex items-center gap-1 text-sm hover:underline"
          >
            {t("migrate.summary.openLegacy")}
            <Icon icon={ExternalLink} size="xs" />
          </a>
        }
      >
        <div className="flex flex-col gap-4">
          <DescriptionList items={facts} orientation="horizontal" />
          {summary.description ? (
            <div className="flex flex-col gap-1">
              <Typography variant="label">{t("migrate.summary.description")}</Typography>
              <Typography variant="body-sm" className="break-words whitespace-pre-wrap">
                {summary.description}
              </Typography>
            </div>
          ) : null}
          <div className="flex flex-col gap-2">
            <Typography variant="label">
              {t("migrate.summary.posts", { n: (summary.posts ?? []).length })}
            </Typography>
            {(summary.posts ?? []).length === 0 ? (
              <Typography variant="body-sm" tone="muted">
                {t("migrate.summary.noPosts")}
              </Typography>
            ) : (
              <ul className="flex flex-col gap-2">
                {(summary.posts ?? []).map((post, index) => (
                  <li key={`${post.created_at ?? ""}-${index}`} className="flex flex-col">
                    <Typography variant="caption" tone="muted">
                      {formatDateTime(post.created_at ?? null)} · {post.author}
                      {(post.images ?? 0) > 0
                        ? ` · ${t("migrate.summary.postImages", { n: post.images ?? 0 })}`
                        : ""}
                    </Typography>
                    <Typography variant="body-sm" className="break-words">
                      {[post.subject, post.preview].filter(Boolean).join(" — ")}
                    </Typography>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </SectionCard>

      {already === null ? (
        <SectionCard title={t("migrate.summary.classification")}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={suggestion.needs_review ? "warning" : "info"} variant="subtle" size="sm">
                {t("migrate.summary.suggested", {
                  confidence: t(`migrate.confidence.${suggestion.confidence ?? "low"}`),
                })}
              </Badge>
              {suggestion.reason ? (
                <Typography variant="caption" tone="muted">
                  {suggestion.reason}
                </Typography>
              ) : null}
            </div>

            <Field name="kind" label={t("migrate.summary.kind")}>
              <RadioGroup
                value={classification.kind}
                onValueChange={(value) =>
                  setClassification((previous) => ({
                    ...previous,
                    kind: value as MigrationKindCode,
                  }))
                }
                className="flex flex-wrap gap-4"
              >
                <Radio.Root value="IN" label={t("migrate.summary.kindIN")} />
                <Radio.Root value="RO" label={t("migrate.summary.kindRO")} />
              </RadioGroup>
            </Field>

            {classification.kind === "IN" ? (
              <div className="grid gap-4 md:grid-cols-2">
                <Field name="service" label={t("migrate.summary.service")} required>
                  <OptionSelect
                    aria-label={t("migrate.summary.service")}
                    placeholder={t("migrate.summary.choose")}
                    value={classification.service}
                    options={(summary.taxonomy ?? []).map((entry) => ({
                      value: entry.service,
                      label: entry.service,
                    }))}
                    onValueChange={(service) =>
                      setClassification((previous) => ({ ...previous, service, category: null }))
                    }
                  />
                </Field>
                <Field name="category" label={t("migrate.summary.category")} required>
                  <OptionSelect
                    aria-label={t("migrate.summary.category")}
                    placeholder={t("migrate.summary.choose")}
                    value={classification.category}
                    options={categories.map((category) => ({ value: category, label: category }))}
                    onValueChange={(category) =>
                      setClassification((previous) => ({ ...previous, category }))
                    }
                  />
                </Field>
              </div>
            ) : (
              <Field
                name="offering"
                label={t("migrate.summary.offering")}
                hint={
                  hint === "catalogUnavailable"
                    ? t("migrate.summary.catalogUnavailable")
                    : hint === "notPublished"
                      ? t("migrate.summary.offeringNotPublished", {
                          name: suggestion.offering_name ?? "",
                        })
                      : undefined
                }
                required
              >
                <OptionSelect
                  aria-label={t("migrate.summary.offering")}
                  placeholder={t("migrate.summary.choose")}
                  value={classification.offeringId}
                  options={offerings.map((offering) => ({
                    value: offering.offering_id,
                    label: offering.name,
                  }))}
                  onValueChange={(offeringId) =>
                    setClassification((previous) => ({ ...previous, offeringId }))
                  }
                />
              </Field>
            )}

            <Field
              name="customer"
              label={t("migrate.summary.customer")}
              hint={
                summary.raised_by.employee
                  ? t("migrate.summary.customerHint")
                  : t("migrate.summary.customerUnresolved", { name: summary.raised_by.name })
              }
              error={noEmail ? t("migrate.summary.customerNoEmail") : undefined}
              required
            >
              <EmployeePicker
                aria-label={t("migrate.summary.customer")}
                value={customer?.recId ?? null}
                defaultDisplayValue={customer?.displayName ?? null}
                onChange={(entry) => {
                  if (entry === null) {
                    setCustomer(null);
                    setNoEmail(false);
                    return;
                  }
                  if (!entry.email) {
                    setNoEmail(true);
                    return;
                  }
                  setNoEmail(false);
                  setCustomer({
                    email: entry.email,
                    displayName: entry.display_name,
                    recId: entry.id,
                  });
                }}
              />
            </Field>
          </div>
        </SectionCard>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" tone="neutral" onClick={onBack}>
          {t("migrate.back")}
        </Button>
        <Button
          disabled={!ready}
          onClick={() => {
            if (ready && customer !== null) {
              onOk(classification, customer);
            }
          }}
        >
          {t("migrate.summary.ok")}
        </Button>
      </div>
    </div>
  );
}
